<template>
  <div class="container-fluid">
    <div class="d-flex justify-content-between mb-3">
      <div>
        <StripeBtn @click="openModal(carrierPriorityModal)"> Available carriers </StripeBtn>
      </div>
      <div class="d-flex align-items-center justify-content-end">
        <span class="d-inline-block mr-2">Reset everything to default</span>
        <StripeBtn @click="resetEverything"> Reset </StripeBtn>
      </div>
    </div>
    <BasePriceTable
      :prices="prices"
      :default-prices="defaultPrices"
      @update-price="updatePrice"
      @remove-price="removePrice"
    />
    <PageFooter @save-prices="savePrices" @add-price="addPrice" />
    <CarrierPriorityModal
      v-if="senderAddressId"
      :id="carrierPriorityModal"
      :entity-id="senderAddressId"
      type="sender_address"
    />
  </div>
</template>

<script lang="ts">
import type { BasePrice } from '@/types/Models/BasePrice'
import type { Address } from '@/types/Models/Customer'
import {
  defineComponent,
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  onMounted,
  watch,
  defineAsyncComponent,
  type Ref,
  type ComputedRef,
} from 'vue'
import { useRoute } from 'vue-router'
import { DEFAULT_LOADER } from '@/const/LoadersNames'
import { CARRIER_PRIORITY_MODAL } from '@/const/ModalTypes'
import { SENDER_ADDRESS } from '@/const/PriceTypes'
import { none } from '@/services/Helpers'
import { openModal, registerModals } from '@/services/Modal'
import { addPrice, removePrice, resetEverything, savePrices, updatePrice } from '@/services/Prices/PriceActions'
import { useCustomersStore } from '@/store/customers'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import { usePricesStore } from '@/store/prices'
import { useUiStore } from '@/store/ui'
import StripeBtn from '@/views/Components/Elements/Buttons/StripeBtn.vue'

export default defineComponent({
  name: 'SenderAddressPrices',
  components: {
    StripeBtn,
    PageFooter: defineAsyncComponent(() => import('@/views/Components/PageParts/PageFooter.vue')),
    BasePriceTable: defineAsyncComponent(() => import('@/views/Staff/BasePrice/BasePriceTable.vue')),
    CarrierPriorityModal: defineAsyncComponent(() => import('@/views/Modals/Assignment/CarrierPriorityModal.vue')),
  },
  setup() {
    const uiStore = useUiStore()
    const pricesStore = usePricesStore()
    const customersStore = useCustomersStore()
    const senderAddressesStore = useCustomersSenderAddressesStore()
    const route = useRoute()

    const defaultPrices: Ref<BasePrice[]> = ref([])
    const carrierPriorityModal: string = CARRIER_PRIORITY_MODAL + 'sender_address'

    const companyId: Ref<string> = computed(() => String(route.params.id))
    const selectedSenderAddress: Ref<Address> = computed(
      () => senderAddressesStore.selectedSenderAddressValue || ({} as Address),
    )
    const senderAddressId: ComputedRef<string> = computed(() => selectedSenderAddress.value?.id || '')
    const prices: ComputedRef<BasePrice[]> = computed(() => pricesStore.prices)

    onBeforeMount(() => {
      uiStore.loaderShow({ name: DEFAULT_LOADER })
      customersStore.fetch(companyId.value)
      senderAddressesStore.fetch(companyId.value)
      customersStore.fetchAll()
      pricesStore.setType(SENDER_ADDRESS)
      pricesStore.setEntityId(senderAddressId.value)

      fetchSenderAddress()
    })

    onBeforeUnmount(() => {
      pricesStore.resetState()
    })

    onMounted(() => {
      registerModals(carrierPriorityModal)
    })

    const fetchSenderAddress = () => {
      if (senderAddressId.value) {
        pricesStore.setEntityId(senderAddressId.value)
        pricesStore
          .fetchDefault(senderAddressId.value)
          .then((res) => {
            defaultPrices.value = res
            pricesStore
              .fetch(senderAddressId.value)
              .then((result) => {
                if (result.length === 0) {
                  pricesStore.setPrices(defaultPrices.value)
                }
              })
              .catch(none)
          })
          .catch(none)
      }
    }

    watch(senderAddressId, fetchSenderAddress)

    return {
      prices,
      defaultPrices,
      carrierPriorityModal,
      selectedSenderAddress,
      senderAddressId,
      addPrice,
      openModal,
      savePrices,
      updatePrice,
      removePrice,
      resetEverything,
    }
  },
})
</script>
